import * as React from 'react'
import { cn } from '../../lib/utils'

export type BadgeVariant = 'default' | 'copper' | 'sage' | 'midnight' | 'success' | 'warning' | 'error'

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-warm-100 text-warm-700',
  copper: 'bg-copper-100 text-copper-600',
  sage: 'bg-sage-100 text-sage-600',
  midnight: 'bg-midnight-100 text-midnight-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
}

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  size?: 'sm' | 'md' | 'lg'
  dot?: boolean
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', dot = false, children, ...props }, ref) => {
    const sizeStyles = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-3 py-1 text-xs',
      lg: 'px-4 py-1.5 text-sm',
    }

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full font-heading font-medium',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {dot && (
          <span
            className={cn('w-1.5 h-1.5 rounded-full', {
              'bg-warm-500': variant === 'default',
              'bg-copper-500': variant === 'copper',
              'bg-sage-500': variant === 'sage',
              'bg-midnight-500': variant === 'midnight',
              'bg-green-500': variant === 'success',
              'bg-amber-500': variant === 'warning',
              'bg-red-500': variant === 'error',
            })}
          />
        )}
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

export { Badge }
